/**
 * Variation picker modal for quick add of variable products.
 */

import { apiFetch, openCartDrawer, announceCart } from './cart-drawer.js';
import { focusWithoutScroll, lockBodyScroll, unlockBodyScroll } from './modal-scroll-lock.js';
import { showToast } from './toast.js';

/** @type {Map<number, Record<string, unknown>>} */
const cache = new Map();

let modalEl = null;
let currentProductId = 0;
let previouslyFocused = null;

function getConfig() {
	return window.doneCommerce || {};
}

function escapeHtml(value) {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function ensureModal() {
	if (modalEl) {
		return modalEl;
	}

	const closeLabel = getConfig().i18n?.close || 'Close';

	modalEl = document.createElement('div');
	modalEl.className = 'dc-variation-modal';
	modalEl.hidden = true;
	modalEl.innerHTML = `
		<div class="dc-modal__backdrop" data-variation-modal-close></div>
		<div
			class="dc-modal__panel dc-variation-modal__panel"
			role="dialog"
			aria-modal="true"
			aria-labelledby="dc-variation-modal-title"
			tabindex="-1"
			data-variation-modal-panel
		>
			<button type="button" class="dc-variation-modal__close" data-variation-modal-close aria-label="${escapeHtml(closeLabel)}">
				<span aria-hidden="true">&times;</span>
			</button>
			<div class="dc-variation-modal__content" data-variation-modal-content></div>
		</div>
	`;

	document.body.appendChild(modalEl);

	modalEl.addEventListener('click', (e) => {
		if (e.target.closest('[data-variation-modal-close]')) {
			closeVariationModal();
		}
	});

	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && modalEl && !modalEl.hidden) {
			closeVariationModal();
		}
	});

	return modalEl;
}

function normalizeAttrKey(key) {
	const k = String(key || '');
	if (!k) {
		return '';
	}
	return k.startsWith('attribute_') ? k : `attribute_${k}`;
}

function selectedAttributes(form) {
	/** @type {Record<string, string>} */
	const attrs = {};
	form.querySelectorAll('[data-variation-attr]').forEach((select) => {
		const key = normalizeAttrKey(select.getAttribute('data-variation-attr'));
		if (key) {
			attrs[key] = String(select.value || '');
		}
	});
	return attrs;
}

function attributeValuesMatch(selected, expected) {
	const a = String(selected || '').trim().toLowerCase();
	const b = String(expected || '').trim().toLowerCase();
	if (!b) {
		return true;
	}
	if (!a) {
		return false;
	}
	return a === b;
}

function findVariation(payload, attrs) {
	const variations = payload.variations || [];
	return variations.find((variation) => {
		const vAttrs = variation.attributes || {};
		const keys = Object.keys(vAttrs);
		if (!keys.length) {
			return false;
		}

		return keys.every((rawKey) => {
			const key = normalizeAttrKey(rawKey);
			const expected = String(vAttrs[rawKey] ?? vAttrs[key] ?? '');
			if (expected === '') {
				// Any selected value is acceptable for "any" attributes.
				return Boolean(attrs[key]);
			}
			return attributeValuesMatch(attrs[key], expected);
		});
	});
}

function renderContent(payload) {
	const content = modalEl?.querySelector('[data-variation-modal-content]');
	if (!content) {
		return;
	}

	const chooseLabel = getConfig().i18n?.chooseOptions || 'Choose options';
	const addLabel = getConfig().i18n?.addToCart || 'Add to cart';
	const attributes = payload.attributes || [];

	const fields = attributes
		.map((attr) => {
			const key = normalizeAttrKey(attr.key || attr.slug || '');
			const options = (attr.options || [])
				.map((option) => {
					const value =
						typeof option === 'object' && option !== null
							? String(option.value ?? '')
							: String(option ?? '');
					const label =
						typeof option === 'object' && option !== null
							? String(option.label ?? option.value ?? '')
							: String(option ?? '');
					if (!value) {
						return '';
					}
					return `<option value="${escapeHtml(value)}">${escapeHtml(label || value)}</option>`;
				})
				.filter(Boolean)
				.join('');
			return `
				<label class="dc-variation-modal__field">
					<span>${escapeHtml(attr.name)}</span>
					<select data-variation-attr="${escapeHtml(key)}" required>
						<option value="">${escapeHtml(chooseLabel)}</option>
						${options}
					</select>
				</label>
			`;
		})
		.join('');

	content.innerHTML = `
		<div class="dc-variation-modal__media">
			<img src="${escapeHtml(payload.image)}" alt="" width="120" height="120" data-variation-image>
		</div>
		<div class="dc-variation-modal__body">
			<h2 id="dc-variation-modal-title" class="dc-variation-modal__title">${escapeHtml(payload.name)}</h2>
			<div class="dc-variation-modal__price" data-variation-price>${payload.price_html || ''}</div>
			<form class="dc-variation-modal__form" data-variation-form>
				${fields}
				<button type="submit" class="dc-btn dc-btn--primary" data-variation-submit disabled>
					${escapeHtml(addLabel)}
				</button>
			</form>
		</div>
	`;

	const form = content.querySelector('[data-variation-form]');
	const submit = content.querySelector('[data-variation-submit]');
	const priceEl = content.querySelector('[data-variation-price]');
	const imageEl = content.querySelector('[data-variation-image]');

	const sync = () => {
		const attrs = selectedAttributes(form);
		const complete = attributes.every((attr) => {
			const key = normalizeAttrKey(attr.key || attr.slug || '');
			return Boolean(attrs[key]);
		});
		const match = complete ? findVariation(payload, attrs) : null;
		const purchasable = Boolean(match?.purchasable);

		if (submit) {
			submit.disabled = !purchasable;
		}
		if (priceEl && match?.price_html) {
			priceEl.innerHTML = match.price_html;
		} else if (priceEl) {
			priceEl.innerHTML = payload.price_html || '';
		}
		if (imageEl && match?.image) {
			imageEl.src = match.image;
		}
	};

	form?.addEventListener('change', sync);
	form?.addEventListener('submit', async (e) => {
		e.preventDefault();
		const attrs = selectedAttributes(form);
		const match = findVariation(payload, attrs);
		if (!match?.purchasable || !submit) {
			return;
		}

		submit.disabled = true;
		const original = submit.textContent;
		submit.textContent = getConfig().i18n?.addingToCart || 'Adding…';

		try {
			const variationPayload = {};
			Object.keys(attrs).forEach((key) => {
				variationPayload[key] = attrs[key];
			});

			await apiFetch('done-commerce/v1/cart/items', {
				method: 'POST',
				body: JSON.stringify({
					product_id: payload.id,
					variation_id: match.id,
					variation: variationPayload,
					quantity: 1,
				}),
			});

			closeVariationModal();
			const message = getConfig().i18n?.addedToCart || 'Added to cart';
			showToast(message);
			openCartDrawer({
				announce: message,
			});
		} catch {
			const fail = getConfig().i18n?.addToCartFailed || 'Unable to add to cart';
			showToast(fail, { variant: 'error' });
			announceCart(fail);
			submit.disabled = false;
			submit.textContent = original;
		}
	});

	sync();
}

async function loadPayload(productId) {
	if (cache.has(productId)) {
		return cache.get(productId);
	}

	const data = await apiFetch(`done-commerce/v1/products/${productId}/variations`);
	cache.set(productId, data);
	return data;
}

export async function openVariationModal(productId) {
	const id = Number(productId);
	if (!id) {
		return;
	}

	currentProductId = id;
	const modal = ensureModal();
	const content = modal.querySelector('[data-variation-modal-content]');
	if (content) {
		content.innerHTML = `<p class="dc-variation-modal__loading">${escapeHtml(getConfig().i18n?.loading || 'Loading…')}</p>`;
	}

	previouslyFocused = document.activeElement;
	modal.hidden = false;
	lockBodyScroll();
	focusWithoutScroll(modal.querySelector('[data-variation-modal-panel]'));

	try {
		const payload = await loadPayload(id);
		if (currentProductId !== id) {
			return;
		}
		renderContent(payload);
	} catch {
		if (content) {
			content.innerHTML = `<p class="dc-variation-modal__error">${escapeHtml(getConfig().i18n?.addToCartFailed || 'Unable to load options')}</p>`;
		}
	}
}

export function closeVariationModal() {
	if (!modalEl || modalEl.hidden) {
		return;
	}

	modalEl.hidden = true;
	unlockBodyScroll();
	currentProductId = 0;
	focusWithoutScroll(previouslyFocused);
	previouslyFocused = null;
}

export function initVariationModal() {
	document.addEventListener('click', (e) => {
		const btn = e.target.closest('[data-choose-options]');
		if (!btn || btn.disabled) {
			return;
		}

		e.preventDefault();
		e.stopPropagation();
		const productId = Number(btn.dataset.productId);
		if (productId) {
			openVariationModal(productId);
		}
	});
}
